/**
 * Client entry — replaces @tanstack/react-start's default one (wired via
 * `tanstackStart({ client: { entry: "client" } })` in vite.config.ts).
 *
 * The only addition over the default is the `await` below: the sections SSR
 * rendered through DecoPageRenderer's *sync* path (no Suspense boundary) must
 * be sync-registered on the client too before `hydrateRoot`, or the first
 * client render takes the React.lazy/Suspense branch instead and React throws
 * a hydration mismatch (#418). See the long comment in src/setup.ts.
 *
 * The list is read back from the markup the server already emits — each section
 * carries `data-manifest-key`, and deferred ones additionally carry
 * `data-deferred="true"` (see DecoPageRenderer). So no extra serialization
 * payload is needed, and the set is symmetric with SSR by construction.
 * Deferred sections are excluded on purpose: SSR rendered their skeleton, and
 * their modules should keep loading on scroll.
 */
import { StrictMode, startTransition } from "react";
import { hydrateRoot } from "react-dom/client";
import { StartClient } from "@tanstack/react-start/client";
import {
  nestedSectionKeysFromDocument,
  registerSectionsSyncFor,
} from "./setup";
import { installStaleChunkReload } from "./sdk/staleChunkReload";
import { settleBodyTail } from "./sdk/hydrationTail";

// Antes de qualquer import(): aba aberta antes de um deploy pede chunk que não
// existe mais (ver src/sdk/staleChunkReload.ts).
installStaleChunkReload();

function sectionsRenderedEagerlyBySsr(): string[] {
  const nodes = document.querySelectorAll<HTMLElement>(
    "section[data-manifest-key]:not([data-deferred])",
  );
  const keys = new Set<string>();
  for (const node of nodes) {
    const key = node.dataset.manifestKey;
    if (key) keys.add(key);
  }
  return [...keys];
}

function hydrate() {
  // Scripts do React encostados no <main> antes de hidratar: os terceiros do
  // GTM caem entre eles durante o parse e o cursor do hidratador ficava num nó
  // que o terceiro remove no meio da hidratação fatiada (#418, T-HYD418B).
  settleBodyTail();
  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <StartClient />
      </StrictMode>,
    );
  });
}

// Failing to preload must not leave the page unhydrated: registerSectionsSyncFor
// already swallows per-section import errors, and this catch covers anything
// else so hydration still runs (worst case a section falls back to the lazy
// path, which is the pre-T21 behaviour).
registerSectionsSyncFor([
  ...sectionsRenderedEagerlyBySsr(),
  // Sections nested in another section's props render inline, with no
  // `data-manifest-key` wrapper of their own, so the scan above can't see
  // them. The server lists them in a <meta> for exactly this reason — see
  // NESTED_SECTIONS_META_NAME in src/setup.ts.
  ...nestedSectionKeysFromDocument(),
]).then(hydrate, (err) => {
  console.error("[client] section preload failed, hydrating anyway:", err);
  hydrate();
});
